import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Phone } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HERO_VIDEO_URL = '/assets/video/hero.mp4';

const StarIcon = ({ filled = true }) => (
  <svg className={`w-5 h-5 ${filled ? 'text-[#FBBC05]' : 'text-gray-300'}`} fill="currentColor" viewBox="0 0 20 20">
    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
  </svg>
);

export const Home = () => {
  const [services, setServices] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const videoRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes, portfolioRes] = await Promise.all([
          axios.get(`${API}/services`),
          axios.get(`${API}/portfolio`)
        ]);
        setServices(servicesRes.data.slice(0, 3));
        setPortfolio(portfolioRes.data.slice(0, 4));
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero Section - Video Background */}
      <section data-testid="hero-video-section" className="relative h-screen w-full overflow-hidden">
        <video
          ref={videoRef}
          data-testid="hero-video"
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src={HERO_VIDEO_URL} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60" />

        <div className="absolute inset-0 flex items-center justify-center">
          <div className="container-custom text-center text-white">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-sm tracking-[0.3em] uppercase mb-4"
            >
              Southwest Florida's Premier
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-5xl md:text-7xl font-serif tracking-tight mb-6"
            >
              Interior Design Studio
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="text-lg md:text-xl font-light max-w-2xl mx-auto mb-10 text-white/90"
            >
              Creating beautiful, personalized interiors that reflect your unique personality
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9 }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link
                to="/portfolio"
                data-testid="hero-portfolio-btn"
                className="btn-primary inline-flex items-center justify-center gap-2"
              >
                View Our Work
                <ArrowRight size={16} />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-contact-btn"
                className="btn-outline border-white text-white hover:bg-white hover:text-foreground inline-flex items-center justify-center"
              >
                Schedule Consultation
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-white border-b border-border">
        <div className="container-custom py-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { number: '18+', label: 'Years of Excellence' },
              { number: '20,000+', label: 'Homes Designed' },
              { number: '4.6', label: 'Google Rating', star: true },
              { number: '8', label: 'Expert Designers' },
            ].map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0 }}
                transition={{ delay: i * 0.1 }}
              >
                <p className="text-3xl md:text-4xl font-serif text-bronze">{stat.number}</p>
                {stat.star && (
                  <div className="flex justify-center gap-0.5 mt-1">
                    {[...Array(5)].map((_, j) => <StarIcon key={j} filled={j < 5} />)}
                  </div>
                )}
                <p className="text-xs tracking-widest uppercase text-muted-foreground mt-2">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About Preview - Asymmetric layout */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-4 items-center">
            {/* Images column */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0 }}
              transition={{ duration: 0.7 }}
              className="lg:col-span-6 relative"
            >
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-7">
                  <img
                    src="/assets/images/provence-dining.jpg"
                    alt="Elegant Dining Room"
                    className="w-full h-[420px] object-cover rounded-2xl"
                  />
                </div>
                <div className="col-span-5 flex flex-col gap-4">
                  <img
                    src="/assets/images/vicenzo-livingrm.jpg"
                    alt="Living Room"
                    className="w-full h-[200px] object-cover rounded-2xl"
                  />
                  <div className="bg-bronze rounded-2xl p-6 flex-1 flex flex-col justify-center text-white">
                    <p className="text-3xl font-serif">40+</p>
                    <p className="text-sm mt-1 text-white/80">Years Combined Experience</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Text column */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0 }}
              transition={{ duration: 0.7 }}
              className="lg:col-span-5 lg:col-start-8"
            >
              <p className="text-sm tracking-[0.3em] uppercase text-bronze mb-4">About Us</p>
              <h2 className="text-3xl md:text-4xl font-serif mb-6 leading-tight">
                Designing Dreams<br />Since 2007
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-5">
                Founded by Charlene Neal, Pure Style has been transforming houses into homes across Southwest Florida for nearly two decades. Our full-service approach combines timeless elegance with the relaxed Florida lifestyle.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-8">
                We listen closely to our clients, understanding their unique personalities and needs, then create spaces that truly reflect who they are.
              </p>
              <Link
                to="/about"
                data-testid="about-preview-link"
                className="inline-flex items-center gap-2 text-bronze font-medium hover:gap-3 transition-all duration-300"
              >
                Meet Our Team
                <ArrowRight size={18} />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Preview - Clean cards with accent */}
      <section className="py-20 md:py-28 bg-[#f5f4f0]">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-14 gap-6">
            <div>
              <p className="text-sm tracking-[0.3em] uppercase text-bronze mb-4">What We Do</p>
              <h2 className="text-3xl md:text-4xl font-serif">Full-Service Interior Design</h2>
            </div>
            <Link
              to="/services"
              data-testid="services-preview-link"
              className="inline-flex items-center gap-2 text-bronze font-medium hover:gap-3 transition-all duration-300 shrink-0"
            >
              All Services
              <ArrowRight size={18} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0 }}
                transition={{ duration: 0.5, delay: index * 0.12 }}
                className="group bg-white rounded-2xl p-8 hover:shadow-xl transition-all duration-500 relative overflow-hidden"
              >
                <div className="absolute top-0 left-0 w-1 h-0 bg-bronze group-hover:h-full transition-all duration-500" />
                <span className="text-6xl font-serif text-[#e8e6e1] block mb-4">0{index + 1}</span>
                <h3 className="text-lg font-serif mb-3 group-hover:text-bronze transition-colors">{service.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {service.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Preview - Modern grid */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container-custom">
          <div className="text-center mb-14">
            <p className="text-sm tracking-[0.3em] uppercase text-bronze mb-4">Our Work</p>
            <h2 className="text-3xl md:text-4xl font-serif">Featured Projects</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {portfolio.map((project, index) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0 }}
                transition={{ duration: 0.4, delay: index * 0.08 }}
                className={`group relative overflow-hidden rounded-xl cursor-pointer ${
                  index === 0 ? 'col-span-2 row-span-2 h-[300px] md:h-[500px]' : 'h-[150px] md:h-[240px]'
                }`}
              >
                <Link to="/portfolio">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-400" />
                  <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6 translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-400">
                    <p className="text-[10px] md:text-xs tracking-widest uppercase text-coral mb-1">{project.category}</p>
                    <h3 className="text-sm md:text-lg font-serif text-white">{project.title}</h3>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link
              to="/portfolio"
              data-testid="portfolio-preview-link"
              className="inline-flex items-center gap-2 px-8 py-4 bg-foreground text-white text-sm tracking-widest uppercase hover:bg-bronze transition-colors duration-300 rounded-full"
            >
              View Full Portfolio
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Google Reviews */}
      <section className="py-20 md:py-28 bg-[#f5f4f0]" data-testid="google-reviews-section">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-serif mb-4">What Our Clients Are Saying</h2>
            <p className="text-muted-foreground max-w-lg mx-auto text-sm">
              Discover why homeowners trust Pure Style for personalized, luxury interior design in Southwest Florida.
            </p>
          </div>

          {/* Google badge */}
          <div className="flex items-center justify-center gap-3 mb-6">
            <svg className="h-6" viewBox="0 0 272 92" xmlns="http://www.w3.org/2000/svg">
              <path d="M115.75 47.18c0 12.77-9.99 22.18-22.25 22.18s-22.25-9.41-22.25-22.18C71.25 34.32 81.24 25 93.5 25s22.25 9.32 22.25 22.18zm-9.74 0c0-7.98-5.79-13.44-12.51-13.44S80.99 39.2 80.99 47.18c0 7.9 5.79 13.44 12.51 13.44s12.51-5.55 12.51-13.44z" fill="#EA4335"/>
              <path d="M163.75 47.18c0 12.77-9.99 22.18-22.25 22.18s-22.25-9.41-22.25-22.18C119.25 34.32 129.24 25 141.5 25s22.25 9.32 22.25 22.18zm-9.74 0c0-7.98-5.79-13.44-12.51-13.44s-12.51 5.46-12.51 13.44c0 7.9 5.79 13.44 12.51 13.44s12.51-5.55 12.51-13.44z" fill="#FBBC05"/>
              <path d="M209.75 26.34v39.82c0 16.38-9.66 23.07-21.08 23.07-10.75 0-17.22-7.19-19.66-13.07l8.48-3.53c1.51 3.61 5.21 7.87 11.17 7.87 7.31 0 11.84-4.51 11.84-13v-3.19h-.34c-2.18 2.69-6.38 5.04-11.68 5.04-11.09 0-21.25-9.66-21.25-22.09 0-12.52 10.16-22.26 21.25-22.26 5.29 0 9.49 2.35 11.68 4.96h.34v-3.61h9.25zm-8.56 20.92c0-7.81-5.21-13.52-11.84-13.52-6.72 0-12.35 5.71-12.35 13.52 0 7.73 5.63 13.36 12.35 13.36 6.63 0 11.84-5.63 11.84-13.36z" fill="#4285F4"/>
              <path d="M225 3v65h-9.5V3h9.5z" fill="#34A853"/>
              <path d="M262.02 54.48l7.56 5.04c-2.44 3.61-8.32 9.83-18.48 9.83-12.6 0-22.01-9.74-22.01-22.18 0-13.19 9.49-22.18 20.92-22.18 11.51 0 17.14 9.16 18.98 14.11l1.01 2.52-29.65 12.28c2.27 4.45 5.8 6.72 10.75 6.72 4.96 0 8.4-2.44 10.92-6.14zm-23.27-7.98l19.82-8.23c-1.09-2.77-4.37-4.7-8.23-4.7-4.95 0-11.84 4.37-11.59 12.93z" fill="#EA4335"/>
              <path d="M35.29 41.19V32H67c.31 1.64.47 3.58.47 5.68 0 7.06-1.93 15.79-8.15 22.01-6.05 6.3-13.78 9.66-24.02 9.66C16.32 69.35.36 53.89.36 34.91.36 15.93 16.32.47 35.3.47c10.5 0 17.98 4.12 23.6 9.49l-6.64 6.64c-4.03-3.78-9.49-6.72-16.97-6.72-13.86 0-24.7 11.17-24.7 25.03 0 13.86 10.84 25.03 24.7 25.03 8.99 0 14.11-3.61 17.39-6.89 2.66-2.66 4.41-6.46 5.1-11.65l-22.49-.01z" fill="#4285F4"/>
            </svg>
            <div className="flex gap-0.5">
              {[...Array(5)].map((_, i) => <StarIcon key={i} filled={i < 5} />)}
            </div>
            <span className="text-lg font-semibold">4.6</span>
            <span className="text-muted-foreground text-sm">Rating</span>
          </div>

          <div className="text-center mb-12">
            <a
              href="https://www.google.com/maps/place/Charlene+Neal+PureStyle/@27.3947,-82.4432,17z"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="google-reviews-link"
              className="inline-flex items-center gap-2 bg-[#1a73e8] hover:bg-[#1557b0] text-white px-6 py-3 rounded-full text-sm font-medium transition-colors"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="white"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
              Read Our Google Reviews
            </a>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { name: "Austin Fralin", initial: "A", color: "bg-[#5c6bc0]", time: "a year ago", quote: "PureStyle has been such a pleasure to work with from start to finish. Charlene and her team designed my mother's home in Canoe Creek and she loves it! I highly recommend Purestyle for your design needs." },
              { name: "Chuck & Denise Rath", initial: "KD", color: "bg-[#e91e63]", time: "3 years ago", quote: "We love PureStyle! We are extremely satisfied with the end result designed by Kelly Raby. She was wonderful in listening to our desires from the beginning and nailed it! We have received numerous comments from guests saying, \u201CThis looks like a model home!\u201D" },
              { name: "Jeremy Wright", initial: "J", color: "bg-[#ef6c00]", time: "3 years ago", quote: "Always an absolutely amazing experience with Pure Style. Our first home, we worked closely with Janet Ladwig, and on our second home we worked closely with Kelly Raby. Our expectations have continuously been exceeded. We couldn\u2019t be happier with the end result of our house being transformed into a home." },
              { name: "Kathy Strasser", initial: "K", color: "bg-[#7b8794]", time: "2 years ago", quote: "Working with Kelly Raby from Pure Style put the finishing touches on our Neal home. Kelly was a pleasure to work with from beginning to end, truly understanding our vision and delivering all the way to the end. Our home feels perfect and shows like a model home." },
              { name: "James Marchini", initial: "J", color: "bg-[#795548]", time: "a year ago", quote: "Our working experience with Kelly and the interior design division was outstanding. Everyone was professional, hands on, and went above and beyond to make sure we their client was happy. They made the planning and transition to our new home seamless." },
              { name: "Mark Ladwig", initial: "M", color: "bg-[#607d8b]", time: "9 years ago", quote: "Biased as I know the staff, but I honestly like the work they do meeting customer's needs." },
            ].map((review, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-7 shadow-sm"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 rounded-full ${review.color} flex items-center justify-center text-white text-sm font-medium`}>
                    {review.initial}
                  </div>
                  <div>
                    <p className="font-semibold text-sm">{review.name}</p>
                    <p className="text-xs text-muted-foreground">{review.time}</p>
                  </div>
                </div>
                <div className="flex gap-0.5 mb-3">
                  {[...Array(5)].map((_, i) => <StarIcon key={i} />)}
                </div>
                <p className="text-foreground/80 leading-relaxed text-sm">{review.quote}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-28 md:py-36 overflow-hidden">
        <img
          src="/assets/images/vicenzo-wetbar.jpg"
          alt="Interior"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative z-10 container-custom">
          <div className="max-w-2xl mx-auto text-center text-white">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0 }}
              className="text-3xl md:text-5xl font-serif mb-6"
            >
              Ready to Transform Your Space?
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0 }}
              transition={{ delay: 0.1 }}
              className="text-white/80 mb-10 max-w-lg mx-auto"
            >
              Let's create something beautiful together. Schedule a consultation with our design team today.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0 }}
              transition={{ delay: 0.2 }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link
                to="/contact"
                data-testid="cta-contact-btn"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-bronze text-white text-sm tracking-widest uppercase hover:bg-bronze/90 transition-colors rounded-full"
              >
                Get Started
                <ArrowRight size={16} />
              </Link>
              <a
                href="tel:+19413281233"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-white/40 text-white text-sm tracking-widest uppercase hover:bg-white/10 transition-colors rounded-full"
              >
                <Phone size={16} />
                (941) 328-1233
              </a>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};
